<template>
  <div class="flex h-screen flex-col">
    <loader :is-loading="loading || isSubmitting || isDeletingFile" />
    <contract-builder-header
      :is-submitting="isSubmitting"
      @show-delete-modal="showDeleteModal = true"
      @save-draft="saveDraft"
      @publish="publish"
    />
    <div class="relative flex h-full overflow-hidden">
      <side-panel
        class="z-10"
        :selected-role="selectedRole"
        :roles="roles.active"
        :selected-offer-template="selectedOfferTemplate"
        :organization-id="organizationId"
        :contract-template-fields="contractTemplateFields"
        :contract-template-offer-template-id="contractTemplateOfferTemplateId"
        @role:select="selectRole"
        @offer-template:select="handleOfferTemplateSelect"
      />
      <div class="h-full w-full overflow-y-auto">
        <view-wrapper>
          <form id="contractForm" autocomplete="off">
            <div class="space-y-6 4k:w-3/4" :class="formWidthClass">
              <lf-card has-padding class="mb-8 space-y-4 py-6">
                <lf-h3>{{ $t("CONTRACTS.CONTRACT_BUILDER.DETAIL") }}</lf-h3>
                <lf-input
                  data-cy="contract-template-name"
                  name="name"
                  :placeholder="$t('COMMON.TEMPLATE_NAME')"
                  :value="activeTemplate?.name"
                  @drop.prevent
                />
                <lf-textarea
                  data-cy="contract-template-description"
                  name="description"
                  :placeholder="`${$t('COMMON.DESCRIPTION')} (${$t(
                    'COMMON.OPTIONAL'
                  )})`"
                  :value="activeTemplate?.description || ''"
                  noresize
                  :character-limit="TEXT_CHARACTER_LIMIT_DEFAULT"
                  rows="2"
                  show-label
                  @drop.prevent
                />
              </lf-card>
              <lf-card has-padding class="mb-8 space-y-4 py-6">
                <lf-h3>
                  {{ $t("CONTRACTS.CONTRACT_BUILDER.DEFINE_ROLES") }}
                </lf-h3>
                <template
                  v-for="(role, index) in roles.active"
                  :key="`${role}_${index}`"
                >
                  <contract-role
                    :index="index"
                    :role="role"
                    :options="roleOptions"
                    :active-roles="roles.active"
                    @dragstart="startDrag"
                    @dragend="stopDrag"
                    @role:change="handleChangeRole($event, index, role)"
                    @role:delete="deleteRole(index)"
                  />
                  <contract-role-dropzone
                    :is-dragging="isDragging"
                    :placeholder="t('CONTRACTS.CONTRACT_BUILDER.DROP_ROLE')"
                    @role:drop="handleDropRole($event, index)"
                  />
                </template>
                <div
                  class="my-10 flex h-12 w-full cursor-pointer flex-col justify-center rounded-sm border-2 border-dashed border-gray-300 text-center text-xxs font-semibold text-primary uppercase"
                  data-cy="add-role"
                  @click="addNextRole"
                >
                  {{ $t("CONTRACTS.CONTRACT_BUILDER.ADD_ROLE") }}
                </div>
              </lf-card>
              <lf-card has-padding class="mb-8 space-y-4 py-6">
                <div class="flex items-center justify-between">
                  <lf-h3>
                    {{ $t("CONTRACTS.CONTRACT_BUILDER.CONTRACT_MAPPING") }}
                  </lf-h3>
                  <button
                    v-if="activeTemplate?.document"
                    class="flex h-10 w-10 cursor-pointer items-center justify-center rounded-sm border hover:text-red-500"
                    data-cy="delete-role"
                    @click.prevent="showDeleteFileModal = true"
                  >
                    <icon-base :icon="IconTrashCanOutline" />
                  </button>
                </div>
                <contract-document-upload
                  v-if="showDocumentUpload"
                  :is-edit="isEdit"
                  @file:uploaded="getContractTemplate()"
                />
                <contract-map-fields
                  v-else-if="
                    activeTemplate && !isEmpty(activeTemplate.document)
                  "
                  :file-url="activeTemplate.document.file_url || ''"
                  :file-id="activeTemplate.document.id"
                  :role="selectedRole"
                  :roles="roles.active"
                  :is-editing="isEditingField"
                  @field:edit="editField"
                  @field:clear="clearField"
                >
                  <div class="mb-2 text-xxs uppercase">
                    {{ activeTemplate.document.original_name || "-" }}
                    &bull;
                    {{ formatDateCustom(activeTemplate.document.created_at) }}
                  </div>
                </contract-map-fields>
              </lf-card>
            </div>
          </form>
        </view-wrapper>
        <div
          v-if="isEditingField && activeField"
          class="absolute top-0 right-0 z-30 h-full w-100 overflow-y-auto bg-white shadow-main"
        >
          <contract-field-edit
            v-if="!loadingAttributes"
            :field="activeField"
            :roles="roles.active"
            :selected-role="selectedRole"
            :attributes="attributes"
            @close="clearField"
            @field:edit="handleEditField"
            @field:delete="handleDeleteField"
          />
        </div>
      </div>
    </div>
  </div>
  <confirm-modal
    v-if="showDeleteModal"
    :title="$t('CONTRACTS.CONTRACT_BUILDER.DELETE_TITLE')"
    :description="
      $t('COMMON.DELETE_CONFIRMATION', { source: $t('CONTRACTS.CONTRACT') })
    "
    :confirm-button="$t('COMMON.DELETE')"
    confirm-button-type="error"
    :close="() => (showDeleteModal = false)"
    @confirm="handleDelete"
  />
  <confirm-modal
    v-if="showDeleteFileModal"
    :title="`${$t('COMMON.DELETE')} ${$t('COMMON.FILE')}`"
    :description="
      $t('COMMON.DELETE_CONFIRMATION', { source: $t('COMMON.FILE') })
    "
    :confirm-button="$t('COMMON.DELETE')"
    confirm-button-type="error"
    :close="() => (showDeleteFileModal = false)"
    @confirm="handleDeleteFile"
  />
</template>

<script setup lang="ts">
import { useWindowSize } from "@vueuse/core";
import { computed, onBeforeMount, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";

import isEmpty from "lodash/isEmpty";
import pick from "lodash/pick";
import { storeToRefs } from "pinia";
import { useForm } from "vee-validate";

import { AnalyticsEvent } from "@/enums/analytics";
import { CustomAttributeStatusOption } from "@/enums/customAttributes";
import { TEXT_CHARACTER_LIMIT_DEFAULT } from "@/helpers/constants";
import {
  CONTRACT_BUILDER_SCHEMA_VERSION,
  TEMPLATE_MODES,
} from "@/helpers/constants/contractBuilder";
import { makeRandomString } from "@/helpers/formatting";
import { formatDateCustom } from "@/helpers/formatting";
import { callStoreAction } from "@/helpers/vee-validate";
import { useAuth } from "@/hooks/auth";
import { useClients } from "@/hooks/clients";
import { usePromiseWrapper } from "@/hooks/common";
import { useContractBuilder } from "@/hooks/contractBuilder";
import { useOfferTemplateFields } from "@/hooks/contractBuilder";
import { useNotification } from "@/hooks/notifications";
import {
  ROUTE_CONTRACT_BUILDER,
  ROUTE_CONTRACT_BUILDER_EDIT,
} from "@/router/routes";
import contractBuilderService from "@/services/modules/contractBuilder";
import offerTemplateService from "@/services/modules/offerTemplate";
import { useAnalyticsStore } from "@/stores/analytics";
import { useContractBuilderStore } from "@/stores/contractBuilder";
import { useAttributeBuilderStore } from "@/stores/customAttributeBuilder";

import ContractBuilderHeader from "@/components/contractBuilder/ContractBuilderHeader.vue";
import SidePanel from "@/components/contractBuilder/ContractBuilderSidePanel.vue";
import ContractDocumentUpload from "@/components/contractBuilder/ContractDocumentUpload.vue";
import ContractFieldEdit from "@/components/contractBuilder/ContractFieldEdit.vue";
import ContractMapFields from "@/components/contractBuilder/ContractMapFields.vue";
import ContractRole from "@/components/contractBuilder/ContractRole.vue";
import ContractRoleDropzone from "@/components/contractBuilder/ContractRoleDropzone.vue";
import IconTrashCanOutline from "@/components/icons/IconTrashCanOutline.vue";
import ConfirmModal from "@/components/modals/ConfirmModal.vue";

import type {
  ContractField,
  ContractFieldWithName,
  ContractRecipient,
  ContractTemplateV2,
  RoleWithID,
} from "@/models/contractBuilder";
import type { OfferTemplate } from "@/models/offers";

const DESKTOP_BREAKPOINT = 2000;

type Roles = {
  all: ContractRecipient["role"][];
  active: RoleWithID[];
};

const values = reactive<ContractTemplateV2>({
  id: "",
  color: "",
  created_at: "",
  description: "",
  is_lendflow_template: false,
  name: "",
  status: 0,
  updated_at: "",
});

const { isLendflowUser } = useAuth();
const { activeClient } = useClients();
const store = useContractBuilderStore();
const { trackEvent } = useAnalyticsStore();
const attributesStore = useAttributeBuilderStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { roleOptions, contractTemplatesOptions } = useContractBuilder();
const { dispatch } = useStore();
const { getAttributes } = attributesStore;
const { showMessage } = useNotification();
const { width } = useWindowSize();
const { attributes } = storeToRefs(attributesStore);
const { activeTemplate } = storeToRefs(store);

const {
  fetchContractTemplateFields,
  clearContractTemplateFields,
  contractTemplateFields,
} = useOfferTemplateFields();

const selectedOfferTemplate = ref<OfferTemplate | null>(null);

const showDeleteModal = ref(false);
const showDeleteFileModal = ref(false);
const isDragging = ref(false);
const selectedRole = ref({
  role: Object.values(roleOptions.value)[0],
  id: makeRandomString(),
});
const isEditingField = ref(false);
const activeField = ref<ContractField | null>(null);
const roles = reactive<Roles>({ all: [], active: [] });

const organizationId = computed(
  () => activeTemplate.value?.client?.id ?? activeClient.value?.id
);

const formWidthClass = computed(() => {
  return !isEditingField.value && width.value < DESKTOP_BREAKPOINT
    ? "w-full"
    : "form-editing";
});

const contractTemplateOfferTemplateId = computed(() => {
  if (!activeTemplate.value?.content?.recipients) {
    return undefined;
  }

  for (const recipient of activeTemplate.value.content.recipients) {
    for (const field of recipient.fields ?? []) {
      if (field.context?.value?.offer_template_id) {
        return field.context.value.offer_template_id;
      }
    }
  }

  return undefined;
});

const backRoute = computed(() => ({
  name: ROUTE_CONTRACT_BUILDER,
}));
const canManageAttributeBuilder = computed(
  () => activeClient.value?.can_manage_attribute_builder
);

const canAccessAttributeBuilder = computed(() => {
  return canManageAttributeBuilder.value || isLendflowUser;
});

const isEdit = computed(() => route.name === ROUTE_CONTRACT_BUILDER_EDIT);

const showDocumentUpload = computed(() => !activeTemplate.value?.document);

const { fetchWrapper: getContractTemplate, loading } = usePromiseWrapper(
  async () => {
    if (!isEdit.value) {
      store.activeTemplate = { ...values };
      return;
    }
    const id = route.params.templateId?.toString();
    if (!id) {
      return;
    }

    const template = await store.getContractTemplate(id, {
      include_content: true,
    });
    store.activeTemplate = template;

    const offerTemplateId = contractTemplateOfferTemplateId.value;
    if (offerTemplateId && !selectedOfferTemplate.value) {
      const response = await offerTemplateService.getTemplate(offerTemplateId);
      if (response?.data) {
        handleOfferTemplateSelect(response.data);
      }
    }

    return template;
  }
);

const { loading: loadingAttributes, fetchWrapper: fetchAttributes } =
  usePromiseWrapper(async () => {
    if (!canAccessAttributeBuilder.value) {
      return;
    }

    await getAttributes({
      status: CustomAttributeStatusOption.published,
    });
  });

const { handleSubmit, isSubmitting } = useForm();

const { fetchWrapper: handleDelete } = usePromiseWrapper(async () => {
  if (!isEdit.value || !route.params.templateId) {
    return;
  }
  await contractBuilderService.deleteContractTemplate(
    route.params.templateId.toString()
  );
  trackEvent(AnalyticsEvent.ContractTemplateDeleted, {
    template_id: route.params.templateId.toString(),
  });
  await router.push(backRoute.value);
});

const { fetchWrapper: handleDeleteFile, loading: isDeletingFile } =
  usePromiseWrapper(async () => {
    if (
      !isEdit.value ||
      !route.params.templateId ||
      !activeTemplate.value?.document?.id
    ) {
      return;
    }
    await contractBuilderService.deleteContractTemplateFile(
      route.params.templateId.toString(),
      activeTemplate.value.document.id
    );
    await getContractTemplate();
  });

const selectRole = (index: number) => {
  selectedRole.value = roles.active[index];
};

const handleOfferTemplateSelect = async (template: OfferTemplate) => {
  selectedOfferTemplate.value = template;
  clearContractTemplateFields();

  if (!template?.id) {
    return;
  }

  await fetchContractTemplateFields(template.id);
};

const createTemplateHandler = (
  status: (typeof TEMPLATE_MODES)[keyof typeof TEMPLATE_MODES]
) => {
  handleSubmit(async (values, actions) => {
    const payload: Partial<ContractTemplateV2> = pick({ ...values }, [
      "name",
      "description",
    ]);
    if (isEdit.value && activeTemplate.value?.id) {
      payload.id = activeTemplate.value.id;
    }
    if (payload.description === "") {
      payload.description = undefined;
    }
    payload.status = status;
    payload.content = {
      version: CONTRACT_BUILDER_SCHEMA_VERSION,
      description: payload.description,
      recipients: getRecipientsPayload(),
    };
    const actionName = isEdit.value
      ? "updateContractTemplate"
      : "createContractTemplate";
    try {
      await callStoreAction(payload, actions, actionName, store);
      if (isEdit.value) {
        trackEvent(AnalyticsEvent.ContractTemplateEdited, {
          template_id: activeTemplate.value?.id,
        });
      } else {
        trackEvent(AnalyticsEvent.ContractTemplateCreated, {
          template_id: store.activeTemplate?.id,
        });
      }
    } catch (error) {
      if ((error as { message: string })?.message?.includes("422")) {
        return;
      }
      const message =
        (error as { message: string })?.message ?? t("COMMON.ERROR_OCCURRED");
      showMessage(message, "error");
    }
    if (!isEdit.value) {
      if (!activeTemplate.value?.id) {
        return;
      }
      await router.push({
        name: ROUTE_CONTRACT_BUILDER_EDIT,
        params: {
          templateId: activeTemplate.value.id,
        },
      });
    }
    getContractTemplate();
  })();
};

const saveDraft = () => createTemplateHandler(TEMPLATE_MODES.draft);

const publish = () => createTemplateHandler(TEMPLATE_MODES.published);

const startDrag = () => {
  isDragging.value = true;
};

const stopDrag = () => {
  isDragging.value = false;
};

const handleDropRole = (event: DragEvent, index: number) => {
  const jsonData = event.dataTransfer?.getData("text");
  if (!jsonData || !isDragging.value) {
    return;
  }
  const data: { index: number; role: RoleWithID } = JSON.parse(jsonData);
  isDragging.value = false;
  if (data.index !== index) {
    const currentIndex = roles.active.findIndex(
      (role) => role.id === data.role.id
    );
    if (currentIndex > -1) {
      roles.active.splice(currentIndex, 1);
    }
    if (index < currentIndex) {
      roles.active.splice(index + 1, 0, data.role);
      return;
    }
    if (currentIndex > -1) {
      roles.active.splice(index, 0, data.role);
      return;
    }
    roles.active.splice(index + 1, 0, data.role);
  }
};

const addNextRole = () => {
  roles.active.push({
    role: roles.all[0],
    id: makeRandomString(),
  });
  isEditingField.value = false;
};

const handleChangeRole = (
  role: RoleWithID,
  index: number,
  oldRole: RoleWithID
) => {
  const currentIndex = roles.active.indexOf(oldRole);
  if (currentIndex > -1) {
    roles.active.splice(currentIndex, 1);
  }
  roles.active.splice(index, 0, role);
  if (roles.active.length === 1) {
    selectedRole.value = roles.active[0];
  }
};

const deleteRole = (index: number) => {
  roles.active.splice(index, 1);
};

const getRecipientsPayload = (): ContractRecipient[] => {
  const recipients = activeTemplate.value?.content?.recipients ?? [];
  return (
    roles.active?.map((role) => {
      const selectedRecipient = recipients.find(
        (recipient) => recipient.id === role.id
      );
      if (!selectedRecipient) {
        return {
          role: role.role,
          id: role.id ?? makeRandomString(),
          fields: [],
        };
      }

      const fields =
        selectedRecipient?.fields.map((field) => {
          field.xPosition =
            typeof field.xPosition === "string"
              ? parseInt(field.xPosition)
              : Math.round(field.xPosition);
          field.yPosition =
            typeof field.yPosition === "string"
              ? parseInt(field.yPosition)
              : Math.round(field.yPosition);
          return pick(field, [
            "id",
            "xPosition",
            "yPosition",
            "pageNumber",
            "required",
            "tabLabel",
            "context",
          ]);
        }) ?? [];
      return {
        role: role.role,
        fields,
        id: selectedRecipient.id ?? makeRandomString(),
      };
    }) || []
  );
};

const findRecipientById = (roleId: string) => {
  return activeTemplate.value?.content?.recipients?.find(
    (recipient) => recipient.id === roleId
  );
};

const handleEditField = (field: ContractFieldWithName) => {
  if (!activeField.value) {
    return;
  }
  const updatedRole = field.role;
  const recipient = findRecipientById(updatedRole.id);
  if (!recipient) {
    return;
  }
  const fieldIndex = recipient.fields.findIndex((f) => f.id === field.id);
  if (fieldIndex === -1) {
    // remove from old role
    const oldRecipient = findRecipientById(selectedRole.value.id);
    const oldFieldIndex = oldRecipient?.fields.findIndex(
      (f) => f.id === field.id
    );
    if (oldFieldIndex !== undefined && oldFieldIndex !== -1) {
      oldRecipient?.fields.splice(oldFieldIndex, 1);
    }
    recipient.fields.push(field);
  }
  selectedRole.value = updatedRole;
  recipient.fields[fieldIndex] = field;
};

const editField = (field: ContractFieldWithName) => {
  const updatedRole = field.role;
  isEditingField.value = true;
  activeField.value =
    findRecipientById(updatedRole.id)?.fields.find((f) => f.id === field.id) ||
    null;
  selectedRole.value = updatedRole;
};

const handleDeleteField = (field: ContractFieldWithName) => {
  if (!activeField.value) {
    return;
  }
  const updatedRole = { role: field.role, id: field.role.id };
  const recipient = findRecipientById(updatedRole.id);
  if (!recipient) {
    return;
  }
  const fieldIndex = recipient.fields.findIndex(
    ({ id }) => id === activeField.value?.id
  );
  if (fieldIndex !== -1) {
    recipient.fields.splice(fieldIndex, 1);
  }
  clearField();
};

const clearField = () => {
  isEditingField.value = false;
  activeField.value = null;
};

const checkAndGetTemplateAndOptions = async () => {
  const promises = [getContractTemplate(), fetchAttributes()];
  if (isEmpty(contractTemplatesOptions.value)) {
    promises.push(dispatch("options/getContractTemplatesOptions"));
  }
  await Promise.all(promises);
};

onBeforeMount(async () => {
  await checkAndGetTemplateAndOptions();
  roles.all = contractTemplatesOptions.value?.recipient_roles ?? [];
  roles.active = activeTemplate.value?.content?.recipients?.map((recipient) => {
    return {
      role: recipient.role,
      id: recipient.id ?? makeRandomString(),
    };
  }) ?? [{ role: roles.all[0], id: makeRandomString() }];
  selectedRole.value = roles.active[0];
});

watch(
  roles,
  () => {
    if (!activeTemplate.value?.content?.recipients) {
      return;
    }
    const payload = getRecipientsPayload();
    store.updateContractTemplateRecipients(payload);
  },
  { deep: true }
);
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.form-editing {
  width: calc(100% - 380px);
}
</style>
